import {
	Button,
	Card,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Select,
	Switch,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import type {
	OutstandingBalanceSummaryParameters,
	SubscriptionActivityParameters,
	SubscriptionActivityReportFormat,
} from '@parkchirp/reporting';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FacilityAutocomplete } from '../inputs';
import { AccountFilter, type AccountFilterValue } from './AccountFilter';
import { FacilityFilter } from './FacilityFilter';
import { FacilityHierarchyFilter, type FacilityHierarchyFilterValue } from './FacilityHierarchyFilter';
import { reportingPanelStyles } from './styles';
import type { ReportFormProps } from './types';

export const SubscriptionActivityReportForm = ({
	createReport,
	isGeneratingReport,
	organizationId,
	onValidationError,
}: ReportFormProps) => {
	const [facilityId, setFacilityId] = useState<string | null>(null);
	const [startMonth, setStartMonth] = useState<number | null>(null);
	const [startYear, setStartYear] = useState<number | null>(null);
	const [endMonth, setEndMonth] = useState<number | null>(null);
	const [endYear, setEndYear] = useState<number | null>(null);
	const [accountId, setAccountId] = useState<number | undefined>();
	const [subscriptionName, setSubscriptionName] = useState<string | undefined>();
	const [accountFilter, setAccountFilter] = useState<AccountFilterValue>({
		type: 'all',
	});

	const [showOutstandingBalances, setShowOutstandingBalances] = useState<boolean>(false);
	const [format, setFormat] = useState<SubscriptionActivityReportFormat>('csv');

	const monthOptions = [
		{ label: 'January', value: '0' },
		{ label: 'February', value: '1' },
		{ label: 'March', value: '2' },
		{ label: 'April', value: '3' },
		{ label: 'May', value: '4' },
		{ label: 'June', value: '5' },
		{ label: 'July', value: '6' },
		{ label: 'August', value: '7' },
		{ label: 'September', value: '8' },
		{ label: 'October', value: '9' },
		{ label: 'November', value: '10' },
		{ label: 'December', value: '11' },
	];

	const getYearOptions = useCallback(() => {
		const optionStartYear = 2021;
		const optionEndYear = new Date().getFullYear() + 1;
		const yearOptions = [];
		for (let i = optionStartYear; i <= optionEndYear; i++) {
			yearOptions.push({ label: i.toString(), value: i });
		}
		return yearOptions;
	}, []);

	const [selectedFacility, setSelectedFacility] = useState<{
		facilityId: string;
		facilityNumber: string;
		facilityName: string;
		integration: string | null;
	}>(null);

	useEffect(() => {
		if (selectedFacility) {
			setFacilityId(selectedFacility.facilityId);
		}
	}, [selectedFacility]);

	const canSubmit = useMemo(() => {
		return Boolean(selectedFacility?.facilityId);
	}, [selectedFacility]);

	const reset = () => {
		setFacilityId(null);
		setStartMonth(null);
		setStartYear(null);
		setEndMonth(null);
		setEndYear(null);
		setAccountId(null);
		setSubscriptionName(null);
		setShowOutstandingBalances(false);
		setFormat('csv');
	};

	const buildParameters = (): SubscriptionActivityParameters => {
		if (!facilityId) {
			throw new Error('Select a facility.');
		}

		const parameters: SubscriptionActivityParameters = {
			facilityId,
			showOutstandingBalances,
			format,
			accountId,
			subscriptionName,
			startMonth,
			startYear,
			endMonth,
			endYear,
		};

		return parameters;
	};

	const validate = () => {
		if (!facilityId) {
			onValidationError('Select a facility.');
			return false;
		}

		return true;
	};

	const submit = async () => {
		if (!validate()) {
			return;
		}

		await createReport({
			reportType: 'subscriptionActivity',
			parameters: buildParameters(),
		});
	};

	return (
		<Card
			variant='outlined'
			sx={(theme) => ({
				...reportingPanelStyles(theme),
				overflow: 'hidden',
				p: 3,
			})}
		>
			<Stack spacing={2.5}>
				<FacilityAutocomplete
					label='Facility'
					organizationId={organizationId}
					onChange={(facility) => {
						if (facility) {
							const { id: facilityId, facilityNumber, facilityName, integration } = facility;
							setSelectedFacility({
								facilityId,
								facilityNumber: facilityNumber.toString(),
								facilityName,
								integration,
							});
						}
					}}
					isForReports
				/>

				<Divider />

				<AccountFilter filter={accountFilter} onChange={setAccountFilter} />

				<Divider />

				<FormControl fullWidth variant='outlined'>
					<InputLabel>Start Month</InputLabel>
					<Select
						label='Start Month'
						value={startMonth ?? ''}
						onChange={(e) => setStartMonth(Number(e.target.value))}
						data-cy='start-month-select-menu'
					>
						{monthOptions.map((month) => (
							<MenuItem key={month.value} value={month.value}>
								{month.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				<Divider />

				<FormControl fullWidth variant='outlined'>
					<InputLabel>Start Year</InputLabel>
					<Select
						label='Start Year'
						value={startYear || ''}
						onChange={(e) => setStartYear(Number(e.target.value))}
						data-cy='start-year-select-menu'
					>
						{getYearOptions().map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				<Divider />

				<FormControl fullWidth variant='outlined'>
					<InputLabel>End Month</InputLabel>
					<Select
						label='End Month'
						value={endMonth || ''}
						onChange={(e) => setEndMonth(Number(e.target.value))}
						data-cy='end-month-select-menu'
					>
						{monthOptions.map((month) => (
							<MenuItem key={month.value} value={month.value}>
								{month.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				<Divider />

				<FormControl fullWidth variant='outlined'>
					<InputLabel>End Year</InputLabel>
					<Select
						label='End Year'
						value={endYear || ''}
						onChange={(e) => setEndYear(Number(e.target.value))}
						data-cy='end-year-select-menu'
					>
						{getYearOptions().map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				<Divider />

				<FormControlLabel
					label='Show Outstanding Balances'
					labelPlacement='start'
					sx={{ justifyContent: 'space-between', ml: 0 }}
					control={
						<Switch
							checked={showOutstandingBalances}
							onChange={(e) => setShowOutstandingBalances(e.target.checked)}
							data-cy='outstanding-balances-switch'
						/>
					}
				/>

				<Divider />

				<Stack spacing={1.5}>
					<Typography variant='subtitle1' fontWeight={600}>
						Output
					</Typography>

					<ToggleButtonGroup
						exclusive
						size='small'
						value={format}
						onChange={(_, nextFormat: SubscriptionActivityReportFormat | null) => {
							if (nextFormat) {
								setFormat(nextFormat);
							}
						}}
					>
						<ToggleButton value='csv'>CSV</ToggleButton>
						<ToggleButton value='xlsx'>Excel</ToggleButton>
					</ToggleButtonGroup>
				</Stack>

				<Stack
					direction='row'
					justifyContent='flex-end'
					spacing={2}
					sx={{
						borderColor: 'divider',
						borderTop: 1,
						pt: 2,
					}}
				>
					<Button onClick={reset} disabled={isGeneratingReport}>
						Reset
					</Button>

					<Button onClick={submit} disabled={!canSubmit || isGeneratingReport}>
						Generate Report
					</Button>
				</Stack>
			</Stack>
		</Card>
	);
};
